import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  SimpleGrid,
  Skeleton,
  Card,
  Group,
  Anchor,
  Button,
  Box,
} from '@mantine/core';
import { IconArrowLeft, IconBooks, IconBuildingChurch } from '@tabler/icons-react';
import {
  usePublicMinistryService,
  usePublicCoursesService,
} from '@actschurch/ccms-sdk';
import { CourseCard } from '@/components/CourseCard';
import { matchesSlug } from '@/utils/slug';

/**
 * Ministry detail page — resolves the ministry by matching the URL slug
 * against <c>slugify(name)</c> on the cached ministry list. Keeps numeric
 * IDs out of the address bar entirely.
 */
export default function MinistryDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const publicMinistryService = usePublicMinistryService();
  const publicCoursesService = usePublicCoursesService();

  const { data: ministries = [], isLoading: loadingMinistry } = useQuery({
    queryKey: ['public', 'ministries'],
    queryFn: () => publicMinistryService.list(),
  });

  const ministry = useMemo(
    () => ministries.find((m) => matchesSlug(slug, { name: m.name })) ?? null,
    [ministries, slug],
  );

  // Enriched view — every row carries categoryName + ministryName so we don't
  // need follow-up lookup joins.
  const { data: courses = [], isLoading: loadingCourses } = useQuery({
    queryKey: ['public', 'courses', 'view', ministry?.ministryID],
    queryFn: () => publicCoursesService.listView(ministry?.ministryID),
    enabled: !!ministry,
  });

  if (loadingMinistry) {
    return (
      <Stack gap="md">
        <Skeleton height={36} width={300} />
        <Skeleton height={140} />
      </Stack>
    );
  }

  if (!ministry) {
    return (
      <Stack gap="md">
        <Title order={3}>Ministry not found</Title>
        <Anchor component={Link} to="/ministries">Back to ministries</Anchor>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Anchor component={Link} to="/ministries" size="sm" c="dimmed">
        <Group gap={4}><IconArrowLeft size={14} />Back to ministries</Group>
      </Anchor>

      <Box
        py={28}
        px="lg"
        style={{
          background: 'linear-gradient(135deg, var(--mantine-color-brand-7), var(--mantine-color-brand-4))',
          borderRadius: 'var(--mantine-radius-lg)',
          color: 'white',
        }}
      >
        <Group gap="md">
          <IconBuildingChurch size={36} />
          <div>
            <Title order={1}>{ministry.name}</Title>
            {ministry.description && (
              <Text mt={4} opacity={0.9}>{ministry.description}</Text>
            )}
          </div>
        </Group>
      </Box>

      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Title order={3}>Courses</Title>
            <Text c="dimmed" size="sm">
              {courses.length} public course{courses.length === 1 ? '' : 's'} in this ministry.
            </Text>
          </div>
          <Button component={Link} to="/courses" variant="subtle">All courses</Button>
        </Group>

        {loadingCourses ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={280} radius="lg" />
            ))}
          </SimpleGrid>
        ) : courses.length === 0 ? (
          <Card withBorder padding="xl" radius="lg">
            <Stack align="center" gap="sm">
              <IconBooks size={36} color="var(--mantine-color-dimmed)" />
              <Text c="dimmed">No public courses in this ministry yet.</Text>
            </Stack>
          </Card>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
            {courses.map((course) => (
              <CourseCard
                key={course.courseID}
                course={course}
                categoryName={course.categoryName}
                categoryColor={course.categoryColor}
                ministryName={course.ministryName ?? ministry.name}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Stack>
  );
}
